import { createLogger } from 'shared/services/logger';

const logger = createLogger('Shared:Services:EventChannel', ['error']);

// An event channel, based loosely on Backbone's implementation with some memory management ideas borrowed
// from Angular. One problem uScope has is that events can happen in several contexts. The global space can
// (theoretically) create multiple uScope instances. Events in each uScope instance's scope don't, for the most
// part, care about events in another context's scope. Similarly, each uScope instance can have multiple images
// open, and the child controls of each image don't really care about events in the other images' scope. To address
// this consideration event channel implementation borrows from Angular's $rootscope service the ability to create
// event channel chains via `var childEvents = events.$new()`.
export function EventChannel () {
  const self = this;
  const allListeners = {};
  const children = [];
  const publicEvents = [];

  /* *****************************************************************************************************
     * Public Methods
     ***************************************************************************************************** */

  /**
     * Bubbles the named events up to all parents of the current channel
     * @param events
     */
  self.publicize = function (...events) {
    events.forEach(event => {
      if (!publicEvents.includes(event)) {
        publicEvents.push(event);
      }
    });
  };

  /**
     * Bind an event to a `callback` function
     * @param {String} name - the name of the event. Since this impl is a singleton make sure it's unique!
     * @param {Function} callback - what to do when the event is triggered
     * @param {*} context - `this` argument for @callback
     * @returns {Function} - handy detacher function; the equivalent of `eventChannel.off(name, callback, context)`
     */
  self.on = function (name, callback, context) {
    if (!allListeners[name]) {
      allListeners[name] = [];
    }

    allListeners[name].push({
      callback: callback,
      context: context,
      ctx: context || this
    });

    return self.off.bind(null, name, callback, context);
  };

  /**
     * Bind an event to a `callback` function and immediately detach
     * @param {String} name - the name of the event. Since this impl is a singleton make sure it's unique!
     * @param {Function} callback - what to do when the event is triggered
     * @param {*} context - `this` argument for @callback
     * @returns {Function} - handy detacher function; the equivalent of `eventChannel.off(name, callback, context)`
     */
  self.once = (name, callback, context) => {
    const done = self.on(name, (...args) => {
      callback.apply(context, args);
      done();
    }, context);
  };

  /**
     * Remove one or many callbacks
     * @param {String} name - the name of the event
     * @param {Function} callback - the specific handler to remove. If null removes all callbacks for the named event
     * @param {*} context - the specific context-bound handler to remove. If null removes all callbacks for the function
     */
  self.off = function (name, callback, context) {
    let retain, event, listeners, i, len;
    if ((!name && !callback && !context) || !allListeners[name]) {
      return;
    }

    listeners = allListeners[name];

    allListeners[name] = retain = [];
    if (callback || context) {
      for (i = 0, len = listeners.length; i < len; i++) {
        event = listeners[i];
        if ((callback && callback !== event.callback && callback !== event.callback._callback) ||
                    (context && context !== event.context)) {
          retain.push(event);
        }
      }
    }

    if (retain.length === 0) {
      delete allListeners[name];
    }
  };

  /**
     * Trigger one or many events, firing all bound callbacks. Callbacks are
     * passed the same arguments as `trigger` is, apart from the event name.
     * "Public Events" will be triggered on the root scope
     * @param {String} name - the name of the event to trigger
     * @param {*} [args] - optional arguments to pass to listeners
     */
  self.trigger = function (name, ...args) {
    const listeners = allListeners[name];
    if (!listeners) {
      triggerRootScopeEvent(name, ...args);
      logger.warn(`EventChannel: Event '${name}' has no bindings.`);
      return;
    }

    logger.log(name, ...args);

    // A difficult-to-believe, but optimized internal dispatch function for
    // triggering events. Tries to keep the usual cases speedy (most internal
    // events have < 3 arguments).
    let ev;
    let i = -1;
    const l = listeners.length;
    const a1 = args[0];
    const a2 = args[1];
    const a3 = args[2];

    try {
      switch (args.length) {
        case 0:
          while (++i < l) (ev = listeners[i]).callback.call(ev.ctx);
          break;
        case 1:
          while (++i < l) (ev = listeners[i]).callback.call(ev.ctx, a1);
          break;
        case 2:
          while (++i < l) (ev = listeners[i]).callback.call(ev.ctx, a1, a2);
          break;
        case 3:
          while (++i < l) (ev = listeners[i]).callback.call(ev.ctx, a1, a2, a3);
          break;
        default:
          while (++i < l) (ev = listeners[i]).callback.apply(ev.ctx, args);
      }
    } catch (ex) {
      logger.error(`Unhandled exception executing ${name}:`, ex);
    }

    triggerRootScopeEvent(name, ...args);
  };

  /**
     * Trigger one or many events, firing all bound callbacks and collecting their return values.
     * "Public Events" are not triggered on the root scope.
     * @param name
     * @param args
     * @returns {Array} An array of return values for each listener
     */
  // TODO: This feels like a code smell. The implementation for this trigger is different than the default trigger, and collecting return values kind of defeats the point of the decoupling that this class offers.
  self.triggerWithReturn = (name, ...args) => {
    const listeners = allListeners[name];
    if (!listeners) {
      return [];
    }

    return listeners.map(listener => listener.callback.apply(listener.ctx, args));
  };

  /**
     * Removes all callbacks bound to a given context
     * @param context
     */
  self.disconnect = function (context) {
    if (!context) {
      return;
    }
    Object.keys(allListeners).forEach(key => {
      const listeners = allListeners[key];
      listeners.forEach(event => {
        if (event.context === context) {
          self.off(key, event.callback, event.context);
        }
      });
    });
  };

  /**
     * Create a child event channel.
     * @returns {EventChannel}
     */
  self.$new = () => {
    const child = new EventChannel();
    child.parent = self;
    children.push(child);
    return child;
  };

  /**
     * Destroys all event listeners on this event channel and all its children. Use when tearing down
     * an element. Use this to assist in the prevention of memory leaks!
     * Note that this method doesn't actually `delete` references to its children; that's left up to
     * the browser's garbage collector.
     */
  self.$destroy = () => {
    self.trigger('$destroy');
    Object.keys(allListeners).forEach((key) => {
      self.off(key);
    });
    children.forEach((child) => {
      child.$destroy();
    });
  };

  /* *****************************************************************************************************
     * Private / Helper Methods
     ***************************************************************************************************** */

  function triggerRootScopeEvent (name, ...args) {
    if (publicEvents.indexOf(name) >= 0) {
      let parent = self.parent;
      while (parent) {
        parent.trigger(name, ...args);
        parent = parent.parent;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/shared/services/EventChannel.js